import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Star, Trash2, Pencil, Image as ImageIcon, Download, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtBytes, media, CATEGORIES } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Uploader } from "./Uploader";

export const Field = ({ label, children }) => (
  <label className="block"><span className="kicker !text-[10px] !text-white/55">{label}</span><div className="mt-1.5">{children}</div></label>
);
export const inputCls = "w-full border border-white/15 bg-[#0b0b0b] px-3 py-2.5 text-sm text-white outline-none focus:border-white/60";

const FIELDS = [["title", "Title (EN)"], ["title_fr", "Title (FR)"], ["description", "Description (EN)", true], ["description_fr", "Description (FR)", true], ["alt", "Alt text (SEO)"], ["location", "Location"], ["album", "Album"], ["camera", "Camera"], ["lens", "Lens"], ["settings", "Settings (e.g. 400mm · f/5.6 · 1/1000s · ISO 800)"], ["copyright", "Copyright"], ["slug", "URL slug"]];

const Editor = ({ photo, onSaved, config }) => {
  const [f, setF] = useState(photo);
  useEffect(() => { setF(photo); }, [photo]);
  if (!photo) return null;
  const save = async () => {
    try {
      const body = Object.fromEntries([...FIELDS.map(([k]) => [k, f[k] ?? ""]), ["category", f.category], ["featured", !!f.featured], ["hero", !!f.hero]]);
      await api.patch(`/admin/photos/${photo.id}`, body);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const o = photo.original || {};
  const v = photo.variants || {};
  return (
    <div className="space-y-4" data-testid="photo-editor">
      {photo.urls?.medium && <img src={media(photo.urls.medium)} alt="" className="w-full object-contain" style={{ maxHeight: 260 }} />}
      <div className="grid grid-cols-2 gap-2 border border-white/10 p-3 font-mono text-[11px] text-white/70" data-testid="photo-technical-info">
        <div>Source: {o.width}×{o.height} px</div><div>{o.format} · {fmtBytes(o.size)}</div>
        <div>Display: {v.display?.width}×{v.display?.height}</div><div>{fmtBytes(v.display?.size)} WebP</div>
        <div className="col-span-2">Colour: {o.color_profile || "sRGB (assumed)"} · {o.mode}</div>
        {o.sha256 && <div className="col-span-2 truncate">Upload SHA-256: {o.sha256}</div>}
        <div className="col-span-2 text-white/50">Original camera file not stored (by design) — only optimized versions.</div>
      </div>
      {FIELDS.map(([k, label, area]) => (
        <Field key={k} label={label}>
          {area ? <textarea rows={3} className={inputCls} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`photo-field-${k}`} />
            : <input className={inputCls} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`photo-field-${k}`} />}
        </Field>
      ))}
      <Field label="Category">
        <select className={inputCls} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="photo-field-category">{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
      </Field>
      <div className="flex gap-6 text-sm text-white/85">
        <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} data-testid="photo-field-featured" /> Featured</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.hero} onChange={(e) => setF({ ...f, hero: e.target.checked })} data-testid="photo-field-hero" /> Homepage hero slide</label>
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={save} className="bg-white px-5 py-2.5 text-sm font-semibold text-black" data-testid="photo-save-button">Save</button>
        {photo.urls?.display && <a href={media(photo.urls.display)} target="_blank" rel="noreferrer" download className="flex items-center gap-2 border border-white/20 px-4 py-2.5 text-sm text-white" data-testid="photo-download-button"><Download size={14} /> Display master</a>}
      </div>
      <div className="border-t border-white/10 pt-4">
        <div className="kicker mb-2">Replace photograph</div>
        <p className="mb-3 text-xs text-white/55">Keeps title, metadata and position. Previous files remain counted in storage.</p>
        <Uploader compact replaceId={photo.id} config={config} onDone={onSaved} />
      </div>
    </div>
  );
};

export const MediaLibrary = ({ config, refreshStorage }) => {
  const [photos, setPhotos] = useState([]);
  const [cat, setCat] = useState("");
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const load = () => api.get("/admin/photos").then((r) => setPhotos(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const list = useMemo(() => photos.filter((p) => !cat || p.category === cat).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), [photos, cat]);
  const move = async (i, dir) => {
    const arr = [...list];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    const ids = arr.map((p) => p.id);
    setPhotos((ps) => ps.map((p) => (ids.includes(p.id) ? { ...p, order: ids.indexOf(p.id) } : p)));
    await api.post("/admin/photos/reorder", { ids }).catch((e) => toast.error(errMsg(e)));
  };
  const toggle = async (p, k) => { await api.patch(`/admin/photos/${p.id}`, { [k]: !p[k] }); load(); };
  const remove = async () => {
    try { const r = await api.delete(`/admin/photos/${del.id}`); toast.success(r.data.note); setDel(null); load(); refreshStorage(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const refreshed = () => { load(); refreshStorage(); };
  return (
    <div data-testid="media-library">
      <div className="mb-8 border border-white/10 bg-[#0f0f0f] p-5">
        <div className="kicker mb-4 flex items-center gap-2"><Sparkles size={12} /> Upload photographs</div>
        <Uploader config={config} onDone={refreshed} />
      </div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {["", ...CATEGORIES].map((c) => (
            <button key={c || "all"} onClick={() => setCat(c)} data-testid={`media-filter-${c || "all"}`}
              className={`rounded-full border px-3 py-1.5 text-xs ${cat === c ? "border-white bg-white text-black" : "border-white/20 text-white/75"}`}>{c || "All"} {c ? `(${photos.filter((p) => p.category === c).length})` : `(${photos.length})`}</button>
          ))}
        </div>
        {!cat && <span className="font-mono text-[11px] text-white/50">Select a category to reorder</span>}
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {list.map((p, i) => (
          <div key={p.id} className="group border border-white/10 bg-[#111]" data-testid={`media-item-${p.id}`}>
            <div className="relative aspect-[4/3] overflow-hidden bg-[#0b0b0b]">
              {p.urls?.thumb ? <img src={media(p.urls.thumb)} alt={p.alt} loading="lazy" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-white/40"><ImageIcon /></div>}
              {p.status !== "ready" && <span className={`absolute left-2 top-2 px-2 py-0.5 font-mono text-[10px] ${p.status === "failed" ? "bg-red-600" : "bg-black/70"} text-white`}>{p.status}</span>}
              <div className="absolute right-2 top-2 flex gap-1">
                <button onClick={() => toggle(p, "featured")} title="Featured" data-testid={`media-feature-${p.id}`} className={`flex h-7 w-7 items-center justify-center ${p.featured ? "bg-[#5d9a35] text-white" : "bg-black/60 text-white/70"}`}><Star size={13} /></button>
              </div>
            </div>
            <div className="p-3">
              <div className="truncate text-sm text-white">{p.title}</div>
              <div className="mt-0.5 font-mono text-[10px] text-white/50">{p.category} · {p.original?.width}×{p.original?.height} · {fmtBytes(p.original?.size)}</div>
              <div className="mt-2 flex items-center justify-between">
                <div className="flex gap-1">
                  {cat && <><button onClick={() => move(i, -1)} data-testid={`media-move-left-${p.id}`} className="p-1 text-white/60 hover:text-white"><ArrowLeft size={14} /></button>
                    <button onClick={() => move(i, 1)} data-testid={`media-move-right-${p.id}`} className="p-1 text-white/60 hover:text-white"><ArrowRight size={14} /></button></>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setEdit(p)} data-testid={`media-edit-${p.id}`} className="p-1 text-white/70 hover:text-white"><Pencil size={14} /></button>
                  <button onClick={() => setDel(p)} data-testid={`media-delete-${p.id}`} className="p-1 text-white/70 hover:text-red-400"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <Sheet open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <SheetContent className="w-full overflow-y-auto border-white/10 bg-[#0b0b0b] text-white sm:max-w-lg">
          <SheetHeader><SheetTitle className="font-display text-white">Edit photograph</SheetTitle></SheetHeader>
          <div className="mt-4"><Editor photo={edit} config={config} onSaved={() => { load(); refreshStorage(); }} /></div>
        </SheetContent>
      </Sheet>
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent className="border-white/10 bg-[#0f0f0f] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove “{del?.title}” from the website?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/65">The photo and its versions disappear from every page. Emergent Object Storage has no delete function, so this does <strong>not</strong> free storage space ({fmtBytes((del?.variants?.display?.size || 0) + (del?.variants?.medium?.size || 0) + (del?.variants?.thumb?.size || 0))} stays counted).</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/20 bg-transparent text-white" data-testid="media-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 text-white hover:bg-red-700" data-testid="media-delete-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
